import {
	IonButton,
	IonButtons,
	IonContent,
	IonFooter,
	IonHeader,
	IonIcon,
	IonLoading,
	IonModal,
	IonPage,
	IonTitle,
	IonToast,
	IonToolbar,
} from "@ionic/react";
import { first, flatten, isEmpty, isNil, last, split } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { SparePartStatusResponse, deletePhoto, postPhoto, updateImage } from "../../api";
import { usePhotoGallery } from "../../hooks";
import { arrowBackOutline, refreshOutline, trashSharp } from "ionicons/icons";
import { catchError, EMPTY, from, of, switchMap } from "rxjs";
import { blobToBase64 } from "../../utils";
import useAuth from "../../context/auth.context";
import { Trans, useTranslation } from "react-i18next";
import { CAMERA_OVERLAY_STYLE, DEFAULT_TOAST_DURATION } from '../../constants';
import { CustomCamera } from '../../components/custom-camera';
import { useSettingsContext } from '../../context';

export interface EditPageProps {
	barcode: string;
	blobId: string;
	externalPath?: string | null;
	isOcr?: boolean;
}

export function EditPage({
	barcode,
	blobId,
	externalPath,
	isOcr,
}: EditPageProps) {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const [saveX, setSaveX] = useState<number>();
	const [saveY, setSaveY] = useState<number>();
	const [coords, setCoords] = useState<number[][]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [disable, setDisable] = useState<boolean>(false);
	const [toastMessage, setToastMessage] = useState<string>("");
	const [isCameraOpen, setIsCameraOpen] = useState<boolean>(false);

	const history = useHistory();
	const location = useLocation();
	const { savePhoto } = usePhotoGallery();
	const { acquireToken, username, validateUser } = useAuth();
	const { t } = useTranslation();
	const { plant, plants, savePlant } = useSettingsContext();

	useEffect(() => {
		validateUser(() => history.push('/signin'));
	}, [username]);

	useEffect(() => {
		setCoords([]);
		drawInitial();

		return () => {
			setLoading(false);
			setDisable(false);
		};
	}, [barcode, blobId, externalPath]);

	function drawImage(imageSource: string) {
		const canvas = canvasRef.current;
		if (canvas && blobId) {
			const context = canvas.getContext("2d");
			if (context) {
				setLoading(true);
				const baseImage = new Image();
				baseImage.src = imageSource;
				const { innerWidth: windowWidth, innerHeight: windowHeight } =
					window;

				baseImage.onerror = function (error) {
					setLoading(false);
					console.error(error);
				};

				baseImage.onload = function () {
					canvas.width = baseImage.width;
					canvas.height = baseImage.height;
					context.drawImage(baseImage, 0, 0, baseImage.width, baseImage.height);
					setLoading(false);
				};
			}
		}
	}

	function startDrawing(ev: any) {
		const canvas = canvasRef.current;

		if (canvas) {
			const rect = canvas.getBoundingClientRect();
			if (isNil(ev.touches) || isNil(ev.touches[0])) return;
			const touch = ev.touches[0];
			const x_rel = touch.clientX - rect.left;
			const y_rel = touch.clientY - rect.top;

			const oldX = touch.pageX - rect.left;
			const oldY = touch.pageY - rect.top;

			const newX = Math.round((x_rel * ev.target.width) / rect.width);
			const newY = Math.round((y_rel * ev.target.height) / rect.height);

			setSaveX(newX);
			setSaveY(newY);

			const newCoords = [...coords];
			newCoords.push([oldX / rect.width, oldY / rect.height]);
			setCoords(newCoords);
		}
	}

	function updateLine(ev: any) {
		const canvas = canvasRef.current;

		if (canvas) {
			const context = canvas.getContext("2d");
			if (context && saveX && saveY) {
				const rect = canvas.getBoundingClientRect();

				if (isNil(ev.touches) || isNil(ev.touches[0])) return;

				const touch = ev.touches[0];
				const x_rel = touch.clientX - rect.left;
				const y_rel = touch.clientY - rect.top;

				const oldX = touch.pageX - rect.left;
				const oldY = touch.pageY - rect.top;

				const currentX = Math.round((x_rel * ev.target.width) / rect.width);
				const currentY = Math.round((y_rel * ev.target.height) / rect.height);

				context.lineJoin = "round";
				context.lineWidth = 5;

				context.beginPath();
				context.moveTo(saveX, saveY);
				context.lineTo(currentX, currentY);
				context.closePath();

				context.stroke();

				setSaveX(currentX);
				setSaveY(currentY);

				const newCoords = [...coords];
				newCoords.push([oldX / rect.width, oldY / rect.height]);
				setCoords(newCoords);
			}
		}
	}

	function resetDrawing() {
		setCoords([]);
		drawInitial();
	}

	function drawInitial() {
		if (
			externalPath !== undefined &&
			externalPath !== null &&
			!isEmpty(externalPath)
		) {
			drawImage(window.atob(externalPath));
		} else {
			drawImage(`blob:${window.location.origin}/${blobId}`);
		}
	}

	function canEdit() {
		return coords.length !== 0;
	}

	function onFinish() {
		if (
			externalPath !== undefined &&
			externalPath !== null &&
			!isEmpty(externalPath)
		) {
			acquireToken()
				.pipe(
					switchMap(accessToken => {
						return updateImage({
							barcode,
							blobId: first(split(blobId, '.'))!,
							drawing: flatten(coords),
							isOcr: isOcr ?? false,
							signedUrl: window.atob(externalPath),
							username: username!,
						}, accessToken)
							.pipe(
								catchError(error => {
									console.error(error);
									setLoading(false);
									setToastMessage(t("editPhoto.savePhotoError"));
									return EMPTY;
								}))

					})
				)
				.subscribe(() => {
					setLoading(false);
					history.push(`/details/${barcode}`);
				});
			history.push(`/details/${barcode}`);
		} else {
			onSave(false).subscribe(() => {
				history.push(`/details/${barcode}`);
			});
		}
	}

	function onSave(isOcr: boolean) {
		setLoading(true);
		setDisable(true);
		const partId = split(location.pathname, "/")[2];
		const flattened = flatten(coords);
		const plant_number = plant;
		return from(fetch(`blob:${origin}/${blobId}`))
			.pipe(
				switchMap(response => response.blob()),
				switchMap(blob => from(blobToBase64(blob))),
				switchMap((base64: string) => {
					return acquireToken().pipe(
						switchMap(accesToken => {
							return postPhoto(partId, base64, isOcr, flattened, accesToken, username!, plant_number)
								.pipe(
									catchError(error => {
										console.error(error);
										setLoading(false);
										setDisable(false);
										setToastMessage(t("editPhoto.savePhotoError"));
										return EMPTY;
									})
								)
						})
					)
				}),
				switchMap((response: SparePartStatusResponse) => {
					if (response.spare_part_count >= response.spare_part_max_count) {
						history.push(`/details/${barcode}`);
						return EMPTY;
					}
					setToastMessage(t("editPhoto.successfullySaved"));
					return of(response);
				}),
			)
	}

	function takePhoto() {
		setIsCameraOpen(true);
	}

	function onSaveAndTakeAnother() {
		onSave(false)
			.subscribe(() => {
				setLoading(false);
				setIsCameraOpen(true);
			});
	}

	function onSaveTextAndTakeAnother() {
		setDisable(true);
		onSave(true)
			.subscribe(() => {
				setLoading(false);
				setIsCameraOpen(true);
			});
	}

	function onRetakePhoto() {
		if (
			externalPath !== undefined &&
			externalPath !== null &&
			!isEmpty(externalPath)
		) {
			setLoading(true);
			acquireToken()
				.pipe(
					switchMap((accessToken) => {
						return deletePhoto(barcode, blobId, accessToken).pipe(
							catchError((error) => {
								console.error(error);
								setLoading(false);
								setToastMessage(t("editPhoto.deletePhotoError"));
								history.push(`/details/${barcode}`);
								return EMPTY;
							})
						);
					})
				)
				.subscribe(() => {
					setLoading(false);
					takePhoto();
				});
		} else {
			takePhoto();
		}
	}

	function onTakePhoto(imageWebPath: string) {
		if (isNil(imageWebPath) || isEmpty(imageWebPath)) {
			onCancelCamera();
			history.push(`/details/${barcode}`);
		} else {
			savePhoto(imageWebPath)
				.subscribe((photo) => {
					setIsCameraOpen(false);
					setLoading(false);
					const blobId = last(split(photo.blobPath, "/"));
					history.replace(`/edit/${barcode}/${blobId}`);
				});
		}
	}

	function onCancelCamera() {
		console.log("Camera view cancelled");
		setIsCameraOpen(false);
		setLoading(false);
	}

	function onDeletePhoto() {
		if (
			externalPath !== undefined &&
			externalPath !== null &&
			!isEmpty(externalPath)
		) {
			setLoading(true);
			acquireToken()
				.pipe(
					switchMap((accessToken) => {
						return deletePhoto(barcode, blobId, accessToken).pipe(
							catchError((error) => {
								console.error(error);
								setLoading(false);
								setToastMessage(t("editPhoto.deletePhotoError"));
								return EMPTY;
							})
						);
					})
				)
				.subscribe(() => {
					setLoading(false);
					history.push(`/details/${barcode}`);
				});
		} else {
			history.push(`/details/${barcode}`);
			setLoading(false);
		}
	}

	function onSaveText() {
		if (externalPath) {
			setLoading(true);
			setDisable(true);
			acquireToken()
				.pipe(
					switchMap(accessToken => {
						return updateImage({
							barcode,
							blobId: first(split(blobId, '.'))!,
							drawing: flatten(coords),
							isOcr: true,
							signedUrl: window.atob(externalPath),
							username: username!,
						}, accessToken)
							.pipe(
								catchError(error => {
									console.error(error);
									setLoading(false);
									setDisable(false);
									setToastMessage(t("editPhoto.savePhotoError"));
									return EMPTY;
								}))
					})
				)
				.subscribe(() => {
					setLoading(false);
					setDisable(false);
					history.push(`/details/${barcode}`);
				});
		}
	}

	function onBack() {
		history.push(`/details/${barcode}`);
	}

	return (
		<IonPage>
			<IonLoading isOpen={loading} message={t("editPhoto.loading")} />
			<IonHeader>
				<IonToolbar>
					<IonButtons slot="start">
						{/* <IonBackButton defaultHref={`/details/${barcode}`} /> */}
						<IonButton onClick={onBack} size="large">
							<IonIcon icon={arrowBackOutline} slot="icon-only" />
						</IonButton>
					</IonButtons>
					<IonTitle>{t("editPhoto.title")}</IonTitle>
					<IonButtons slot="end">
						<IonButton color="primary" onClick={() => onFinish()}>
							{t("general.save")}
						</IonButton>
					</IonButtons>
				</IonToolbar>
			</IonHeader>
			<IonContent fullscreen scrollY={false}>
				<canvas
					onMouseDown={startDrawing}
					onMouseMove={updateLine}
					onTouchMove={updateLine}
					onTouchStart={startDrawing}
					ref={canvasRef}
					style={{
						display: "block",
						marginLeft: "auto",
						marginRight: "auto",
						paddingLeft: 0,
						paddingRight: 0,
						height: "100%",
					}}
				/>
			</IonContent>
			<IonFooter>
				<IonButton
					color="primary"
					disabled={!canEdit()}
					expand="block"
					onClick={resetDrawing}
				>
					<IonIcon icon={refreshOutline} slot="end" />
					{t("editPhoto.resetDrawing")}
				</IonButton>
				<div
					style={{
						display: "flex",
					}}
				>
					<IonButton
						className="ion-text-wrap"
						color="danger"
						expand="block"
						onClick={onDeletePhoto}
						style={{
							flex: 1,
						}}
					>
						<IonIcon icon={trashSharp} slot="end" />
						{t("editPhoto.deletePhoto")}
					</IonButton>
					<IonButton
						className="ion-text-wrap"
						color="danger"
						expand="block"
						onClick={onRetakePhoto}
						style={{
							flex: 1,
						}}
					>
						<IonIcon icon={trashSharp} slot="end" />
						{t("editPhoto.retakePhoto")}
					</IonButton>
				</div>
				{externalPath !== undefined &&
					externalPath !== null &&
					!isEmpty(externalPath) &&
					!isOcr && (
						<IonButton
							color="primary"
							expand="block"
							onClick={onSaveText}
						>
							{t("editPhoto.saveTextPhoto")}
						</IonButton>
					)}
				{(externalPath === undefined ||
					externalPath === null ||
					isEmpty(externalPath)) &&
					!disable && (
						<div
							style={{
								display: "flex",
							}}
						>
							<IonButton
								className="ion-text-wrap"
								color="primary"
								expand="block"
								onClick={onSaveTextAndTakeAnother}
								style={{
									flex: 1,
								}}
							>
								<Trans
									components={{
										separator: <br></br>,
									}}
									i18nKey="editPhoto.saveTextPhotoAndAddNew"
								/>
							</IonButton>
							<IonButton
								className="ion-text-wrap"
								color="primary"
								expand="block"
								onClick={onSaveAndTakeAnother}
								style={{
									flex: 1,
								}}
							>
								<Trans
									components={{
										separator: <br></br>,
									}}
									i18nKey="editPhoto.savePhotoAndAddNew"
								/>
							</IonButton>
						</div>
					)}
			</IonFooter>
			<IonModal
				isOpen={isCameraOpen}
				onDidDismiss={onCancelCamera}
				style={CAMERA_OVERLAY_STYLE}
			>
				<CustomCamera
					onDismiss={onTakePhoto}
				/>
			</IonModal>
			<IonToast
				color="primary"
				duration={1000}
				isOpen={!isEmpty(toastMessage)}
				message={toastMessage}
				onDidDismiss={() => setToastMessage('')}
			/>
		</IonPage>
	);
}
